import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sun,
  Zap,
  Battery,
  Building2,
  DollarSign,
  Users,
  HardHat,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Shield,
  Globe,
  Clock,
  Target,
  TrendingUp,
  Landmark,
  Cable,
  Bolt,
  MapPin,
  XCircle,
  Handshake,
  FileText,
  Rocket,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#002B7F]/10 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#002B7F]/5 transition-colors"
      >
        <span className="font-bold text-[#002B7F] pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#C0C0C0] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const faqs = [
  {
    question: "Where does the federal funding come from?",
    answer:
      "The federal government has allocated hundreds of billions of dollars through legislation like the Bipartisan Infrastructure Law and the Inflation Reduction Act for infrastructure, energy, and grid modernization projects. These funds are available to states that apply with qualified, shovel-ready projects. Nevada's goal is to be first in line — every time.",
  },
  {
    question: "How does selling energy to other states work?",
    answer:
      "When Nevada produces more clean energy than it consumes — especially solar — that surplus can be transmitted via high-voltage lines to neighboring states like California, Arizona, and Utah. Those states pay for the energy, bringing revenue directly into Nevada. This is how Nevada becomes a regional energy exporter, not just a consumer.",
  },
  {
    question: "Will this raise taxes?",
    answer:
      "No. The strategy is designed to use state matching funds to unlock 2x to 5x more in federal investment. Every state dollar is leveraged to bring back far more. The goal is to bring Nevada's own tax dollars back home — not raise new ones.",
  },
  {
    question: "What kind of jobs does this create?",
    answer:
      "Thousands of construction jobs during the build phase — solar installation, transmission line construction, battery facility assembly, and grid upgrades. Then long-term permanent jobs in operations, maintenance, energy management, and administration. These are real, good-paying careers.",
  },
  {
    question: "How does this lower energy costs?",
    answer:
      "More energy supply means more competition and more stability. Solar energy has no fuel cost — once built, it produces power at near-zero marginal cost. Battery storage smooths out price spikes. And grid resilience prevents the kind of outages and emergencies that drive costs up. More supply, more stability, lower bills.",
  },
];

const FederalFundingPage = () => {
  return (
    <div data-testid="federal-funding-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-28 bg-[#002B7F]">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <Bolt className="w-4 h-4" />
            Federal Funding & Infrastructure
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6 max-w-3xl">
            Maximizing Federal Funding & Building Nevada's Energy Future
          </h1>
          <p className="text-lg text-[#C0C0C0] max-w-2xl mb-8">
            My name is Cooper, and I'm running for Governor of Nevada to make
            Nevada the best state in the country to live, work, and raise a
            family. This is my plan to maximize federal funding, lower energy
            costs, create jobs, and make Nevada the clean energy capital of
            America.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-strategy"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Full Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>
        </div>
      </section>

      {/* ── SECTION 1: THE OPPORTUNITY ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-6">
            The Opportunity
          </h2>

          <p className="text-slate-600 leading-relaxed mb-4">
            Right now, the federal government is investing hundreds of billions
            of dollars into infrastructure and energy. This is one of the
            largest federal investment windows in American history.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            But most states fail to capture their fair share. Not because they
            don't need it. Not because they don't deserve it.
          </p>
          <p className="text-slate-600 leading-relaxed mb-6">
            Because they're not prepared.
          </p>

          <div className="bg-[#002B7F] p-6 text-center mb-8">
            <p className="text-white font-bold text-lg">
              "The states that win federal funding aren't the ones that need it
              most — they're the ones that are ready."
            </p>
          </div>

          <div className="bg-[#D4AF37]/15 border-2 border-[#D4AF37]/30 p-6">
            <p className="text-[#002B7F] font-bold text-lg">
              Nevada will be ready.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: WE POWER THE SOUTHWEST ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Sun className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              We Power the Southwest
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                icon: Sun,
                title: "Unmatched Solar Potential",
                desc: "Nevada receives more direct sunlight than nearly any state in the country — a natural advantage no other state can replicate",
              },
              {
                icon: MapPin,
                title: "Available Land",
                desc: "Vast open land ideal for large-scale solar farms, battery facilities, and transmission infrastructure",
              },
              {
                icon: Globe,
                title: "Regional Demand",
                desc: "California, Arizona, and Utah are growing fast and need clean energy — Nevada is positioned to supply it",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white/10 border border-white/20 p-6"
                >
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-white text-sm mb-2">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-white/10 border-2 border-[#D4AF37]/40 p-6 text-center mb-8">
            <p className="text-[#D4AF37] font-bold text-xl">
              "Nevada won't just power itself — we will power the Southwest."
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                title: "Sell Energy to Other States",
                desc: "Export surplus clean energy to neighboring states that need it",
              },
              {
                title: "Bring Billions Into Nevada",
                desc: "Energy exports generate revenue that stays in our state",
              },
              {
                title: "Regional Energy Hub",
                desc: "Nevada becomes the center of Southwest energy infrastructure",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/10 border border-white/20 p-5"
              >
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mb-2" />
                <h3 className="font-bold text-white text-sm mb-1">
                  {item.title}
                </h3>
                <p className="text-white/60 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3: THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-6">
            Why States Fail
          </h2>

          <p className="text-slate-600 leading-relaxed mb-8">
            Federal funding doesn't go to the states that need it most. It goes
            to the states that are organized, prepared, and fast. Most states
            are none of those things.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                icon: Clock,
                title: "Slow Government",
                desc: "Bureaucratic processes that take months or years to approve projects",
              },
              {
                icon: FileText,
                title: "No Project Pipeline",
                desc: "No pre-approved projects ready to submit when funding opens",
              },
              {
                icon: XCircle,
                title: "Not Shovel-Ready",
                desc: "Projects stuck in planning when they should be breaking ground",
              },
              {
                icon: DollarSign,
                title: "Can't Match Funding",
                desc: "No strategy to put up state dollars that unlock federal investment",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex items-start gap-3 p-4 bg-red-50 border border-red-200"
                >
                  <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-red-800 text-sm">
                      {item.title}
                    </p>
                    <p className="text-red-700 text-xs">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold text-lg">
              "And they leave billions of dollars on the table."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: THE STRATEGY ── */}
      <section
        id="the-strategy"
        className="py-16 md:py-24 bg-[#002B7F]/5 scroll-mt-32"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Target className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              The Strategy
            </h2>
          </div>

          <p className="text-slate-600 leading-relaxed mb-4 text-center max-w-2xl mx-auto">
            If we want to win federal funding, we have to build projects the
            right way. Not small projects. Not ideas on paper.
          </p>

          <div className="bg-[#002B7F] p-8 text-center mb-8">
            <p className="text-white font-bold text-lg">
              "We build infrastructure systems that qualify for the largest
              federal investments."
            </p>
          </div>

          <p className="text-slate-600 leading-relaxed text-center max-w-2xl mx-auto">
            That means thinking bigger. Building smarter. And moving faster
            than every other state in the country.
          </p>
        </div>
      </section>

      {/* ── SECTION 5: THE 4-PART ENERGY SYSTEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              The 4-Part Energy System
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A comprehensive energy infrastructure built to win federal
              funding, power the region, and lower costs for Nevadans.
            </p>
          </div>

          <div className="space-y-6">
            {/* 1. Solar */}
            <div className="bg-[#F8F9FA] border-2 border-[#002B7F]/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-black text-lg">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#002B7F] mb-3">
                    Solar Generation
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    Expand large-scale solar generation across Nevada. We have
                    the sun. We have the land. We build at scale — not in small
                    pilot programs, but massive solar installations that produce
                    real power for real people.
                  </p>
                  <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/30 px-4 py-2">
                    <Sun className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-[#002B7F] font-bold text-sm">
                      Nevada's natural advantage — more sun than almost any state
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Transmission */}
            <div className="bg-[#002B7F] p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-black text-lg">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-3">
                    Transmission Expansion
                  </h3>
                  <p className="text-white/80 leading-relaxed mb-3">
                    This is the piece most states overlook — and it's the most
                    important. High-voltage transmission lines that connect
                    Nevada's energy production to regional markets. Without
                    transmission, solar power has nowhere to go. With it, Nevada
                    becomes the energy supplier for the entire Southwest.
                  </p>
                  <div className="bg-white/10 border-2 border-[#D4AF37]/40 p-4 mb-3">
                    <p className="text-[#D4AF37] font-bold">
                      "Transmission is what turns energy projects into
                      infrastructure — and that's where the real federal money
                      is."
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      "High-voltage interstate lines",
                      "Regional grid connections",
                      "Export capacity to CA, AZ, UT",
                      "Qualifies for largest federal grants",
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                        <span className="text-white/90 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Battery Storage */}
            <div className="bg-[#F8F9FA] border-2 border-[#002B7F]/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-black text-lg">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#002B7F] mb-3">
                    Battery Storage
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    Store energy when the sun is shining. Deploy it when demand
                    is highest. Battery storage stabilizes costs, ensures
                    availability around the clock, and makes Nevada's energy
                    system reliable — not just productive.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {[
                      { label: "Store surplus energy", icon: Battery },
                      { label: "Stabilize costs", icon: TrendingUp },
                      { label: "24/7 availability", icon: Shield },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.label}
                          className="flex items-center gap-2 bg-white border border-slate-200 p-3"
                        >
                          <Icon className="w-4 h-4 text-[#002B7F] flex-shrink-0" />
                          <span className="text-slate-700 text-xs font-bold">
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Grid Resilience */}
            <div className="bg-[#F8F9FA] border-2 border-[#002B7F]/10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-black text-lg">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#002B7F] mb-3">
                    Grid Resilience
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    A grid that can handle extreme heat, prevent outages, and
                    support Nevada's growth for decades. Not a patch on the
                    existing system — a modern, resilient energy backbone built
                    for the future.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {[
                      "Handle extreme heat",
                      "Prevent outages",
                      "Support population growth",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-2 bg-white border border-slate-200 p-3"
                      >
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-slate-700 text-xs font-bold">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: HOW WE MAXIMIZE FEDERAL FUNDING ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Landmark className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              How We Maximize Federal Funding
            </h2>
            <p className="text-[#C0C0C0] max-w-2xl mx-auto">
              Five strategies to make sure Nevada captures every dollar it
              deserves.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                icon: Target,
                title: "Federal Funding Strike Team",
                desc: "A dedicated state team whose only job is to identify, apply for, and win federal grants. Full-time. Aggressive. Relentless.",
              },
              {
                icon: Rocket,
                title: "Shovel-Ready Projects",
                desc: "Always have pre-approved, permitted, designed projects ready to submit the moment funding opens. When other states are starting paperwork, Nevada is breaking ground.",
              },
              {
                icon: DollarSign,
                title: "Matching Funds Strategy",
                desc: "Use state dollars strategically to unlock 2x to 5x more in federal investment. Every dollar Nevada puts up brings back multiples.",
              },
              {
                icon: Zap,
                title: "Faster Permitting",
                desc: "Make Nevada the fastest state in America to permit and build energy infrastructure. Speed is a competitive advantage — we will use it.",
              },
              {
                icon: Handshake,
                title: "Public-Private Partnerships",
                desc: "Combine state resources, federal funding, and private investment into projects too large for any one sector alone. This is how you build at scale.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white/10 border border-white/20 p-5"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm mb-1">
                        {item.title}
                      </h3>
                      <p className="text-white/70 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SECTION 7: WHAT THIS MEANS FOR NEVADANS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Users className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              What This Means for Nevadans
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: HardHat,
                title: "More Jobs",
                desc: "Thousands of construction jobs and long-term careers in energy operations, maintenance, and management",
                color:
                  "bg-emerald-50 border-emerald-200 text-emerald-800",
                iconColor: "text-emerald-600",
              },
              {
                icon: DollarSign,
                title: "Lower Energy Costs",
                desc: "More supply means more stability. Solar has no fuel cost. Battery storage prevents price spikes.",
                color: "bg-blue-50 border-blue-200 text-blue-800",
                iconColor: "text-blue-600",
              },
              {
                icon: TrendingUp,
                title: "Economic Growth",
                desc: "Affordable, reliable energy attracts businesses and industry to Nevada — more companies, more jobs, more opportunity",
                color: "bg-amber-50 border-amber-200 text-amber-800",
                iconColor: "text-amber-600",
              },
              {
                icon: Landmark,
                title: "More Federal Money",
                desc: "Bring Nevada's tax dollars back home instead of watching them flow to states that are more prepared",
                color:
                  "bg-violet-50 border-violet-200 text-violet-800",
                iconColor: "text-violet-600",
              },
              {
                icon: Shield,
                title: "Energy Independence",
                desc: "Control our own energy future instead of depending on out-of-state suppliers and volatile markets",
                color: "bg-rose-50 border-rose-200 text-rose-800",
                iconColor: "text-rose-600",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`border p-5 ${item.color}`}
                >
                  <Icon className={`w-6 h-6 ${item.iconColor} mb-3`} />
                  <h3 className="font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-sm opacity-80 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SECTION 8: WHY THIS PLAN WINS ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-8 text-center">
            Why This Plan Wins
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Other states */}
            <div className="bg-red-50 border-2 border-red-200 p-8">
              <h3 className="text-lg font-bold text-red-800 mb-6">
                Other States
              </h3>
              <div className="space-y-4">
                {[
                  "Slow to act",
                  "Unprepared when funding opens",
                  "No shovel-ready projects",
                  "Miss deadlines and opportunities",
                  "Leave federal money on the table",
                ].map((item, i) => (
                  <div key={item} className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-red-800 font-medium text-sm">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Nevada */}
            <div className="bg-emerald-50 border-2 border-emerald-200 p-8">
              <h3 className="text-lg font-bold text-emerald-800 mb-6">
                Nevada Under This Plan
              </h3>
              <div className="space-y-4">
                {[
                  "Ready before funding opens",
                  "Fastest permitting in America",
                  "Strategic matching fund approach",
                  "Dedicated federal funding team",
                  "Winning — every single time",
                ].map((item, i) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-800 font-medium text-sm">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE VISION ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-8">
            The Vision
          </h2>

          <div className="space-y-4 mb-10">
            {[
              "Nevada will become the clean energy capital of America.",
              "We will power the Southwest.",
              "We will bring billions of federal dollars into our state.",
              "And we will use that to create jobs, lower costs, and build a stronger future for every Nevadan.",
            ].map((line, i) => (
              <p
                key={line}
                className="text-lg text-[#002B7F] font-bold leading-relaxed"
              >
                {line}
              </p>
            ))}
          </div>

          <div className="bg-[#002B7F] p-8">
            <p className="text-white font-bold text-lg">
              "This is how Nevada wins. Not by waiting. By building."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Build Nevada's Energy Future
          </h2>
          <p className="text-[#C0C0C0] mb-8">
            More jobs. Lower costs. Billions in federal investment. A stronger
            Nevada.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/pro-union"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Pro-Union <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FederalFundingPage;
